import type { TarotCard } from '@lilith-arcana-os/domain';

import {
  CardLibraryEntrySchema,
  CardSummarySchema,
  type CardLibraryEntry,
  type CardSummary,
} from './types';
import { normalizeToken, uniq } from './shared';

export interface CardDiscoveryMetadata {
  summary_short: string;
  theme_tags: string[];
  search_tokens: string[];
}

const firstSentence = (text: string): string => {
  const [sentence] = text.split('. ');
  return sentence?.trim().replace(/\.$/, '') ?? text.trim();
};

const arcanaLabel = (card: TarotCard): string =>
  card.arcana === 'major' ? 'arcano maggiore' : `${card.suit?.toLowerCase() ?? 'arcano'} ${card.court ? 'di corte' : 'numerato'}`;

export function buildCardDiscoveryMetadata(card: TarotCard): CardDiscoveryMetadata {
  const theme_tags = uniq([
    arcanaLabel(card),
    card.element.toLowerCase(),
    card.suit?.toLowerCase() ?? '',
    card.zodiac_sign?.toLowerCase() ?? '',
    card.sephirah?.toLowerCase() ?? '',
    card.qliphah?.toLowerCase() ?? '',
    card.keywords_up[0] ?? '',
    card.keywords_up[1] ?? '',
  ]).slice(0, 8);

  const search_tokens = uniq([
    card.name,
    card.slug,
    card.hermetic_title,
    card.element,
    card.suit ?? '',
    card.rank ?? '',
    card.astro_primary,
    card.zodiac_sign ?? '',
    card.sephirah ?? '',
    card.qliphah ?? '',
    ...card.keywords_up,
    ...card.keywords_rev,
    ...theme_tags,
  ]).map(normalizeToken);

  return {
    summary_short: `${card.name}: ${firstSentence(card.meaning_up)}.`,
    theme_tags,
    search_tokens,
  };
}

export function createLibraryEntry(card: TarotCard): CardLibraryEntry {
  const discovery = buildCardDiscoveryMetadata(card);
  const emphasis = card.arcana === 'major' ? 'struttura archetipica' : 'movimento del seme';

  return CardLibraryEntrySchema.parse({
    card_id: card.id,
    slug: card.slug,
    summary_short: discovery.summary_short,
    summary_long: `${card.name} viene studiata come ${emphasis}. In biblioteca non è solo “significato”: è clima, gesto, sistema di corrispondenze e ritmo con cui la carta chiede di essere vissuta. ${card.meaning_up}`,
    upright_expanded: `Da diritta, ${card.name} attiva ${card.keywords_up.join(', ')}. ${card.meaning_up} Nello studio lungo conviene osservare come la carta distribuisce energia tra simbolo, posizione e tono della domanda.`,
    reversed_expanded: `Da rovesciata, ${card.name} non perde potere: lo torce, lo comprime o lo rimanda. ${card.meaning_rev} In lettura profonda questa inversione segnala spesso il punto in cui la persona resiste al proprio stesso passaggio.`,
    love_expanded: `${card.meaning_love} In biblioteca viene letta come qualità relazionale: ritmo del desiderio, stile del patto, modalità con cui si cerca vicinanza o distanza.`,
    work_expanded: `${card.meaning_work} Sul piano del mestiere la carta mostra anche quale postura convenga assumere: iniziativa, analisi, cura, disciplina o capacità di chiudere un ciclo.`,
    spirit_expanded: `${card.meaning_spirit} Nello studio rituale si osserva come questa carta modifichi presenza, attenzione e soglia tra gesto concreto e orientamento interiore.`,
    psyche_expanded: `${card.meaning_psyche} Per il lavoro di journaling è utile trattarla come una figura dinamica: non etichetta il soggetto, ma mostra il movimento psichico che sta cercando forma.`,
    symbolism_expanded: `${card.simbologia} Nel dettaglio simbolico conta la relazione tra postura, paesaggio, colore e direzione dello sguardo. La carta parla tanto con la sua scena quanto con le sue parole.`,
    caution_notes: `Evita letture assolute o predittive. ${card.name} non impone un destino lineare: indica una qualità di passaggio, un nodo di coscienza e il prezzo specifico richiesto dalla sua energia.`,
    journaling_prompts: [
      `Dove riconosco nella mia giornata il tema di ${card.keywords_up[0] ?? card.name.toLowerCase()}?`,
      `Quale gesto concreto incarnerebbe ${card.name} senza teatralità?`,
      `Cosa sto proteggendo quando compare la sua ombra: ${card.keywords_rev[0] ?? 'resistenza'}?`,
    ],
    ritual_prompts: [
      `Dedica sette minuti a un gesto semplice che renda visibile ${card.name} nel corpo: postura, respiro, parola o silenzio.`,
      `Scrivi una frase di patto con l energia della carta e leggila prima di chiudere la pratica.`,
    ],
    study_notes: [
      `Astro principale: ${card.astro_primary}.`,
      `Elemento dominante: ${card.element}.`,
      `Asse qliphothico evocato: ${card.qliphah ?? card.lilith_qliphah_link ?? 'nessuno esplicito'}.`,
    ],
    theme_tags: discovery.theme_tags,
    search_tokens: discovery.search_tokens,
  });
}

export function createLibraryEntries(cards: TarotCard[]): CardLibraryEntry[] {
  return cards.map(createLibraryEntry);
}

export function createCardSummary(card: TarotCard): CardSummary {
  const discovery = buildCardDiscoveryMetadata(card);

  return CardSummarySchema.parse({
    card_id: card.id,
    slug: card.slug,
    name: card.name,
    arcana: card.arcana,
    suit: card.suit,
    element: card.element,
    summary_short: discovery.summary_short,
    symbol_emoji: card.symbol_emoji,
    keywords: uniq([...card.keywords_up, ...card.keywords_rev]).slice(0, 5),
  });
}
